import { Alert, Card, Switch, Typography } from 'antd';
import type { UploadEstimate } from '@entities/source';
import { formatRub } from '../lib/formatRub';

const { Text } = Typography;

interface Props {
  estimate: UploadEstimate;
  processImages: boolean;
  onProcessImagesChange: (value: boolean) => void;
}

/**
 * Карточка показа оценки стоимости + Vision-тумблер.
 *
 * Активная цена (по тумблеру) выводится крупно. Вторая — мелким серым,
 * чтобы юзер понимал альтернативную стоимость.
 *
 * Дефолт тумблера — ON (FULL-локализации тоже ON, см. backlog).
 */
export function EstimateCard({ estimate, processImages, onProcessImagesChange }: Props) {
  const activeCost = processImages
    ? estimate.estimated_cost_with_vision_rub
    : estimate.estimated_cost_without_vision_rub;
  const inactiveCost = processImages
    ? estimate.estimated_cost_without_vision_rub
    : estimate.estimated_cost_with_vision_rub;

  const insufficient = activeCost > estimate.current_balance_rub;
  const hasImages = estimate.images_count > 0;

  return (
    <Card size="small" styles={{ body: { padding: 16 } }}>
      <div style={{ marginBottom: 12 }}>
        <Text strong>{estimate.file_name ?? 'Файл'}</Text>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {estimate.page_count} {pluralPages(estimate.page_count)}
            {hasImages && `, ${estimate.images_count} ${pluralImages(estimate.images_count)}`}
          </Text>
        </div>
      </div>

      {hasImages && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 12px',
            background: '#F9FAFB',
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 13, color: '#1A1A1A' }}>Обрабатывать картинки</div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Vision разберёт изображения в документе
            </Text>
          </div>
          <Switch checked={processImages} onChange={onProcessImagesChange} />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 4,
        }}
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          К списанию
        </Text>
        <Text strong style={{ fontSize: 18 }}>
          {formatRub(activeCost)}
        </Text>
      </div>

      {hasImages && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 8,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {processImages ? 'Без обработки картинок' : 'С обработкой картинок'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatRub(inactiveCost)}
          </Text>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: 8,
          borderTop: '1px solid #F3F4F6',
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Баланс
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatRub(estimate.current_balance_rub)}
        </Text>
      </div>

      {insufficient && (
        <Alert
          type="info"
          showIcon
          message="Спишется при пополнении баланса"
          description="Документ обработается сейчас. Стоимость спишется с баланса аккаунта — пополните его перед публикацией в каналах."
          style={{ marginTop: 12 }}
        />
      )}
    </Card>
  );
}

function pluralPages(n: number): string {
  const last = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'страниц';
  if (last === 1) return 'страница';
  if (last >= 2 && last <= 4) return 'страницы';
  return 'страниц';
}

function pluralImages(n: number): string {
  const last = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'картинок';
  if (last === 1) return 'картинка';
  if (last >= 2 && last <= 4) return 'картинки';
  return 'картинок';
}
